import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable, getFunctions } from "firebase/functions";
import { Icons } from "../../lib/Icons";

const SoloCheckoutPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/signup/solo");
        return;
      }

      setUser(currentUser);

      // Check if already subscribed
      try {
        const functions = getFunctions(undefined, 'europe-west3');
        const checkSubscription = httpsCallable(functions, 'checkSoloSubscriptionFunc');
        const result = await checkSubscription();
        
        if (result.data.hasActiveSubscription) {
          navigate("/solo/dashboard");
          return;
        }
      } catch (e) {
        console.log('Subscription check failed:', e);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleStartCheckout = async () => {
    setLoading(true);
    setError("");

    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const createCheckout = httpsCallable(functions, 'createCheckoutSessionFunc');
      
      const result = await createCheckout({
        successUrl: window.location.origin,
        cancelUrl: window.location.origin + '/solo/checkout',
      });

      // Redirect to Stripe Checkout
      if (result.data.url) {
        window.location.href = result.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e) {
      console.error('Checkout error:', e);
      setError(e.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOGM5Ljk0MSAwIDE4LTguMDU5IDE4LTE4cy04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAzIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

      <div className="relative flex min-h-screen flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed" 
                className="w-12 h-12 rounded-xl"
              />
              <div className="text-left">
                <h1 className="text-2xl font-bold text-white">SEN-Ed Resources</h1>
                <p className="text-sm text-purple-200">Individual Teacher Access</p>
              </div>
            </div>
          </div>

          {/* Pricing Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
            {/* Plan header */}
            <div className="bg-gradient-to-r from-purple-500/30 to-pink-500/30 p-6 text-center">
              <h2 className="text-2xl font-bold text-white mb-2">Complete Your Registration</h2>
              <p className="text-purple-200">Access hundreds of SEN teaching resources</p>
            </div>

            <div className="p-6">
              {/* Pricing */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold text-white">£4.99</span>
                  <span className="text-purple-200">/month</span>
                </div>
                <div className="mt-2">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full">
                    <Icons.Sparkles className="w-5 h-5 text-green-300" />
                    <span className="text-green-100 font-semibold">First 30 days FREE</span>
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-4 mb-8">
                {[
                  { Icon: Icons.BookOpen, text: "100+ SEN teaching activities" },
                  { Icon: Icons.Film, text: "Video resources for every strategy" },
                  { Icon: Icons.School, text: "Primary & Secondary sections" },
                  { Icon: Icons.Search, text: "AI-powered resource search" },
                  { Icon: Icons.Sparkles, text: "New resources added weekly" },
                  { Icon: Icons.Check, text: "Cancel anytime, no questions asked" },
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <feature.Icon className="w-5 h-5 text-purple-300" />
                    <span className="text-purple-100">{feature.text}</span>
                  </div>
                ))}
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/20 rounded-xl border border-red-400/30">
                  <p className="text-red-100 text-sm">{error}</p>
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={handleStartCheckout}
                disabled={loading}
                className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 text-white font-bold text-lg shadow-lg hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </span>
                ) : (
                  "Subscribe Now →"
                )}
              </button>

              {/* Trust badges */}
              <div className="mt-6 flex items-center justify-center gap-4 text-purple-300/70 text-sm">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Secure checkout
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Powered by Stripe
                </span>
              </div>
            </div>
          </div>

          {/* Logged in as */}
          {user && (
            <p className="mt-6 text-center text-sm text-purple-200">
              Signed in as {user.email}
            </p>
          )}

          {/* Back link */}
          <button
            onClick={() => navigate('/signup/solo')}
            className="mt-4 block mx-auto text-sm text-purple-200 hover:text-white transition-colors"
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoloCheckoutPage;


